import { log } from '@/lib/logger'



import * as React from "react"
import { Globe, Phone, Filter, X, Star, Zap, Flag, Heart, Eye, Bell, Bookmark, Check, Circle, Cloud, Compass, Flame, Gift, Hash, Key, Leaf, Map, Moon, Music, Paperclip, Pen, Pin, Plus, Rocket, Search, Settings, Shield, Smile, Sparkles, Square, Sun, ThumbsUp, Trash, User, Users, Wifi, MessageCircle, ChevronLeft, ChevronRight, Loader2, Store, DollarSign, Users2, Building2, Briefcase, Pencil } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { useTranslations } from "@/hooks/use-translations"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Input } from "@/components/ui/input"
import { useConversationFilters, type ConversationFilterResponse } from "@/hooks/use-conversation-filters"
import { tagService, type Tag } from "@/services/TagService"
import { META_INTEGRATION_ENABLED } from "@/config/env"
import { useIsMobile } from "@/hooks/use-mobile"
import type { ChannelCatalogEntry } from "@/services/conversationsApi"
import { registryKeyToSelectedId } from "@/lib/channel-inbox"
import { COLOR_DE_CANAL, COLOR_DE_CATEGORIA, estiloDeCanal } from '@/lib/colores-de-canal'
import { PastillaConAcciones } from '@/components/ui/pastilla-con-acciones'
import { SelectorVisual } from '@/components/ui/selector-visual'
import { TagBadge } from '@/components/ui/tag-badge'
import { TarjetaElegible } from '@/components/ui/tarjeta-elegible'
import { MessengerIcon } from './messenger-icon'

interface PlatformFiltersProps {
  selectedPlatform: string
  onSelectPlatform: (platform: string) => void
  onApplyFilter?: (filter: { name: string; icon: string; tags: string[]; channels: string[]; agents: string[]; id?: string; user_categories: string[] }) => void
  /** From GET /api/v1/channels/ — when null/empty, legacy static tabs are used. */
  channelCatalog?: ChannelCatalogEntry[] | null
  pinnedChannelKeys?: string[]
  onTogglePinChannelKey?: (key: string) => void
}

/** Los 39 iconos entre los que puede elegir un filtro guardado. `renderIcon`,
 *  mas abajo, es quien sabe pintarlos. */
const ICONOS_DE_FILTRO = [
  "filter", "star", "zap", "flag", "heart", "eye", "message", "phone", "globe",
  "bell", "bookmark", "check", "circle", "cloud", "compass", "flame", "gift",
  "hash", "key", "leaf", "map", "moon", "music", "paperclip", "pen", "pin",
  "rocket", "search", "settings", "shield", "smile", "square", "sun",
  "thumbs-up", "trash", "user", "users", "wifi", "x",
] as const

const platforms = [
  {
    id: "all",
    name: "All",
    icon: null,
  },
  {
    id: "web",
    name: "web",
    icon: <Globe className="h-4 w-4 text-primary" />,
  },
  {
    id: "betterplace",
    name: "Betterplace",
    icon: <Building2 className="h-4 w-4" style={estiloDeCanal('betterplace')} />,
  },
  {
    id: "phone_call",
    name: "phone_call",
    icon: <Phone className="h-4 w-4" style={estiloDeCanal('phone_call')} />,
  },
  {
    id: "whatsapp",
    name: "whatsapp",
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke={COLOR_DE_CANAL.whatsapp}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-4 w-4"
      >
        <path d="M3 21l1.9-5.7a8.5 8.5 0 113.8 3.8z" />
      </svg>
    ),
  },
  {
    id: "form-leads",
    name: "Form-Leads",
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 32 32"
        fill={COLOR_DE_CANAL.facebook}
        className="h-4 w-4"
      >
        <path d="M5,19.5c0-4.6,2.3-9.4,5-9.4c1.5,0,2.7,0.9,4.6,3.6c-1.8,2.8-2.9,4.5-2.9,4.5c-2.4,3.8-3.2,4.6-4.5,4.6  C5.9,22.9,5,21.7,5,19.5 M20.7,17.8L19,15c-0.4-0.7-0.9-1.4-1.3-2c1.5-2.3,2.7-3.5,4.2-3.5c3,0,5.4,4.5,5.4,10.1  c0,2.1-0.7,3.3-2.1,3.3S23.3,22,20.7,17.8 M16.4,11c-2.2-2.9-4.1-4-6.3-4C5.5,7,2,13.1,2,19.5c0,4,1.9,6.5,5.1,6.5  c2.3,0,3.9-1.1,6.9-6.3c0,0,1.2-2.2,2.1-3.7c0.3,0.5,0.6,1,0.9,1.6l1.4,2.4c2.7,4.6,4.2,6.1,6.9,6.1c3.1,0,4.8-2.6,4.8-6.7  C30,12.6,26.4,7,22.1,7C19.8,7,18,8.8,16.4,11" />
      </svg>
    ),
  },
]

// Mapping for category icons to match User Profile
const CATEGORY_ICONS = {
  client: Store,
  lead: DollarSign,
  collaborator: Users2,
  employee: Building2,
  vendor: Briefcase,
}

const PHONE_CALL_REGISTRY_KEY = "Phone Call"

function catalogChannelGlyph(entry: ChannelCatalogEntry, extraClass?: string) {
  const ic = (entry.icon || "").toLowerCase()
  if (ic === "betterplace") {
    return <Building2 className={cn("h-4 w-4", extraClass)} style={estiloDeCanal('betterplace')} />
  }
  if (ic === "web") {
    return <Globe className={cn("h-4 w-4 text-primary", extraClass)} />
  }
  if (ic === "whatsapp") {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke={COLOR_DE_CANAL.whatsapp}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={cn("h-4 w-4", extraClass)}
      >
        <path d="M3 21l1.9-5.7a8.5 8.5 0 113.8 3.8z" />
      </svg>
    )
  }
  if (ic === "instagram") {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke={COLOR_DE_CANAL.instagram}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={cn("h-4 w-4", extraClass)}
      >
        <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
        <path d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37z" />
        <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
      </svg>
    )
  }
  if (ic === "facebook") {
    return <MessengerIcon className={extraClass} />
  }
  if (ic === "phone") {
    return <Phone className={cn("h-4 w-4", extraClass)} style={estiloDeCanal('phone_call')} />
  }
  if (ic === "avatar") {
    return <Sparkles className={cn("h-4 w-4 text-primary", extraClass)} />
  }
  if (ic === "wordpress") {
    return <Globe className={cn("h-4 w-4", extraClass)} style={estiloDeCanal('wordpress')} />
  }
  return <MessageCircle className={cn("h-4 w-4 text-muted-foreground", extraClass)} />
}

export function PlatformFilters({
  selectedPlatform,
  onSelectPlatform,
  onApplyFilter,
  channelCatalog = null,
  pinnedChannelKeys = [],
  onTogglePinChannelKey,
}: PlatformFiltersProps) {
  const { t } = useTranslations()
  const isMobile = useIsMobile()
  const [displayMode, setDisplayMode] = React.useState<"full" | "compact" | "scroll">("scroll")
  const scrollContainerRef = React.useRef<HTMLDivElement>(null)
  const [showBuilder, setShowBuilder] = React.useState(false)
  const [canScrollLeft, setCanScrollLeft] = React.useState(false)
  const [canScrollRight, setCanScrollRight] = React.useState(true)
  const [filterName, setFilterName] = React.useState("")
  const [selectedIcon, setSelectedIcon] = React.useState("filter")
  const [emoji, setEmoji] = React.useState("")
  const [tags, setTags] = React.useState<string[]>([])
  const [channels, setChannels] = React.useState<string[]>([]) // Start empty
  const [userCategories, setUserCategories] = React.useState<string[]>([]) // User category filter
  const [editingFilterId, setEditingFilterId] = React.useState<string | null>(null)


  // Fetched tags from backend
  const [fetchedTags, setFetchedTags] = React.useState<Tag[]>([])
  
  // Search state for tags
  const [tagSearchValue, setTagSearchValue] = React.useState<string>("")

  // Use the backend-powered hook for saved filters
  const { filters: savedFilters, createFilter: createFilterApi, updateFilter: updateFilterApi, deleteFilter: deleteFilterApi, isCreating, deletingId } = useConversationFilters()
  const safeSavedFilters = Array.isArray(savedFilters) ? savedFilters : []

  const [iconColorClass, setIconColorClass] = React.useState<string>("")
  const [pickerOpen, setPickerOpen] = React.useState(false)

  React.useEffect(() => {
    const loadTags = async () => {
      try {
        const tagsData = await tagService.getTags()
        if (tagsData) {
          setFetchedTags(tagsData)
        }
      } catch (err) {
        console.error("Failed to load tags for filter builder", err)
      }
    }
    loadTags()
  }, [])

  // Memoize filtered tags for better performance and debugging
  const filteredAvailableTags = React.useMemo(() => {
    const searchTerm = tagSearchValue.trim().toLowerCase();
    const selectedTagNames = new Set(tags);
    
    if (!searchTerm) {
      // Show all tags that aren't selected
      return fetchedTags.filter(tag => !selectedTagNames.has(tag.name));
    }
    
    // Filter by search term and exclude already selected tags
    const filtered = fetchedTags.filter(tag => {
      const tagNameLower = tag.name.toLowerCase().trim();
      const matchesSearch = tagNameLower.includes(searchTerm);
      const notSelected = !selectedTagNames.has(tag.name);
      return matchesSearch && notSelected;
    });
    
    // Debug logging when search returns no results
    if (searchTerm && filtered.length === 0 && fetchedTags.length > 0) {
      log.debug('🔍 Tag search debug:', {
        searchTerm,
        fetchedTagsCount: fetchedTags.length,
        selectedTags: tags,
        fetchedTagNames: fetchedTags.map(t => t.name),
        matchingTags: fetchedTags.filter(tag => 
          tag.name.toLowerCase().trim().includes(searchTerm)
        ).map(t => t.name)
      });
    }
    
    return filtered;
  }, [fetchedTags, tagSearchValue, tags]);


  // measure overflow to show borders only when needed
  const suggestRef = React.useRef<HTMLDivElement>(null)
  const [showBorderSuggest, setShowBorderSuggest] = React.useState(false)

  const recomputeBorders = React.useCallback(() => {
    const s = suggestRef.current
    if (s) setShowBorderSuggest(s.scrollWidth > s.clientWidth)
  }, [])

  React.useEffect(() => {
    recomputeBorders()
  }, [tags, tagSearchValue, recomputeBorders])

  React.useEffect(() => {
    const onResize = () => recomputeBorders()
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [recomputeBorders])

  const renderIcon = React.useCallback((iconId: string, extraClass?: string) => {
    if (iconId?.startsWith('emoji:')) {
      return <span className={cn("text-lg leading-none", extraClass)}>{iconId.replace('emoji:', '')}</span>
    }
    switch (iconId) {
      case 'phone': return <Phone className={cn("h-4 w-4", extraClass)} style={estiloDeCanal('phone_call')} />;
      case 'globe': return <Globe className={cn("h-4 w-4 text-primary", extraClass)} />;
      case 'star': return <Star className={cn("h-4 w-4", extraClass)} />;
      case 'zap': return <Zap className={cn("h-4 w-4", extraClass)} />;
      case 'flag': return <Flag className={cn("h-4 w-4", extraClass)} />;
      case 'heart': return <Heart className={cn("h-4 w-4", extraClass)} />;
      case 'eye': return <Eye className={cn("h-4 w-4", extraClass)} />;
      case 'bell': return <Bell className={cn("h-4 w-4", extraClass)} />;
      case 'bookmark': return <Bookmark className={cn("h-4 w-4", extraClass)} />;
      case 'check': return <Check className={cn("h-4 w-4", extraClass)} />;
      case 'circle': return <Circle className={cn("h-4 w-4", extraClass)} />;
      case 'cloud': return <Cloud className={cn("h-4 w-4", extraClass)} />;
      case 'compass': return <Compass className={cn("h-4 w-4", extraClass)} />;
      case 'flame': return <Flame className={cn("h-4 w-4", extraClass)} />;
      case 'gift': return <Gift className={cn("h-4 w-4", extraClass)} />;
      case 'hash': return <Hash className={cn("h-4 w-4", extraClass)} />;
      case 'key': return <Key className={cn("h-4 w-4", extraClass)} />;
      case 'leaf': return <Leaf className={cn("h-4 w-4", extraClass)} />;
      case 'map': return <Map className={cn("h-4 w-4", extraClass)} />;
      case 'moon': return <Moon className={cn("h-4 w-4", extraClass)} />;
      case 'music': return <Music className={cn("h-4 w-4", extraClass)} />;
      case 'paperclip': return <Paperclip className={cn("h-4 w-4", extraClass)} />;
      case 'pen': return <Pen className={cn("h-4 w-4", extraClass)} />;
      case 'pin': return <Pin className={cn("h-4 w-4", extraClass)} />;
      case 'rocket': return <Rocket className={cn("h-4 w-4", extraClass)} />;
      case 'search': return <Search className={cn("h-4 w-4", extraClass)} />;
      case 'settings': return <Settings className={cn("h-4 w-4", extraClass)} />;
      case 'shield': return <Shield className={cn("h-4 w-4", extraClass)} />;
      case 'smile': return <Smile className={cn("h-4 w-4", extraClass)} />;
      case 'square': return <Square className={cn("h-4 w-4", extraClass)} />;
      case 'sun': return <Sun className={cn("h-4 w-4", extraClass)} />;
      case 'thumbs-up': return <ThumbsUp className={cn("h-4 w-4", extraClass)} />;
      case 'trash': return <Trash className={cn("h-4 w-4", extraClass)} />;
      case 'user': return <User className={cn("h-4 w-4", extraClass)} />;
      case 'users': return <Users className={cn("h-4 w-4", extraClass)} />;
      case 'wifi': return <Wifi className={cn("h-4 w-4", extraClass)} />;
      case 'message':
      case 'message-circle': return <MessageCircle className={cn("h-4 w-4", extraClass)} />;
      case 'x': return <X className={cn("h-4 w-4", extraClass)} />;
      default: return <Filter className={cn("h-4 w-4", extraClass)} />;
    }
  }, [])

  const createFilterData = React.useCallback(() => {
    const iconValue = selectedIcon.startsWith('emoji') ? selectedIcon : (emoji.trim() ? `emoji:${emoji.trim()}` : selectedIcon)
    return {
      name: (filterName || 'Filtro').trim(),
      icon: iconValue,
      icon_color: iconColorClass,
      tag_names: tags,
      channels: channels.map(c => c === 'web' ? 'Web' : c === 'whatsapp' ? 'WhatsApp' : c === 'phone_call' ? 'Phone Call' : c === 'betterplace' ? 'Betterplace' : c),
      user_categories: userCategories,
      match_type: 'all' as const,
    }
  }, [selectedIcon, emoji, filterName, tags, channels, userCategories, iconColorClass])

  // Reset form when dialog closes
  const resetForm = React.useCallback(() => {
    setFilterName('')
    setSelectedIcon('filter')
    setEmoji('')
    setTags([])
    setChannels([])
    setUserCategories([])
    setIconColorClass('')
    setEditingFilterId(null)
    setTagSearchValue('')
  }, [])

  const handleEdit = React.useCallback((filter: ConversationFilterResponse) => {
    setEditingFilterId(filter.id)
    setFilterName(filter.name)
    setSelectedIcon(filter.icon || 'filter')
    if (filter.icon?.startsWith('emoji:')) {
      setEmoji(filter.icon.replace('emoji:', ''))
    }
    setIconColorClass(filter.icon_color || '')
    setTags(filter.tag_names || [])

    // Reverse mapping for channels
    const reverseChannels = (filter.channels || []).map((c: string) =>
      c === 'Web' ? 'web' :
        c === 'WhatsApp' ? 'whatsapp' :
          c === 'Phone Call' ? 'phone_call' : c === 'Betterplace' ? 'betterplace' : c
    )
    setChannels(reverseChannels)
    setUserCategories(filter.user_categories || [])

    setShowBuilder(true)
  }, [])

  React.useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    // El ancho de este contenedor depende de `displayMode` (colapsa etiquetas,
    // activa overflow-x, muestra flechas), y `displayMode` se calcula a partir
    // de ese mismo ancho. Sin proteccion eso forma un bucle observar->mutar->
    // observar que hace flip-flop cada frame cuando el ancho cae justo en un
    // umbral. En pantallas a devicePixelRatio 1.0/1.25 (Windows 100-125%) el
    // ancho aterriza en el umbral y la barra "vibra" (las palabras bailan);
    // en Retina (DPR 2) el redondeo la deja estable. Se corta con: (1) histeresis
    // (banda muerta ~40px alrededor de 400/700 mayor que el salto que provoca un
    // cambio de modo), (2) batido en rAF y (3) salir si el ancho no cambio.
    const classify = (
      width: number,
      prev: "full" | "compact" | "scroll",
    ): "full" | "compact" | "scroll" => {
      if (prev === "compact") return width > 440 ? (width >= 720 ? "full" : "scroll") : "compact"
      if (prev === "full") return width < 660 ? (width < 380 ? "compact" : "scroll") : "full"
      // prev === "scroll"
      if (width >= 720) return "full"
      if (width < 380) return "compact"
      return "scroll"
    }

    const refreshScrollAffordances = () => {
      setCanScrollLeft(container.scrollLeft > 0)
      setCanScrollRight(container.scrollLeft + container.clientWidth < container.scrollWidth - 1)
    }

    let rafId = 0
    let lastWidth = -1

    const evaluate = () => {
      const width = container.clientWidth
      if (width === lastWidth) {
        refreshScrollAffordances()
        return
      }
      lastWidth = width
      if (isMobile) {
        setDisplayMode((prev) => (prev === "scroll" ? prev : "scroll"))
      } else {
        setDisplayMode((prev) => classify(width, prev))
      }
      refreshScrollAffordances()
    }

    const resizeObserver = new ResizeObserver(() => {
      if (rafId) return
      rafId = requestAnimationFrame(() => {
        rafId = 0
        evaluate()
      })
    })

    const handleScroll = () => refreshScrollAffordances()

    resizeObserver.observe(container)
    container.addEventListener('scroll', handleScroll)
    evaluate()
    handleScroll()

    return () => {
      if (rafId) cancelAnimationFrame(rafId)
      resizeObserver.disconnect()
      container.removeEventListener('scroll', handleScroll)
    }
  }, [isMobile, safeSavedFilters.length])

  const useCatalogDriven = Array.isArray(channelCatalog) && channelCatalog.length > 0
  const pinnedSafe = pinnedChannelKeys

  const catalogPrimary = React.useMemo(() => {
    if (!useCatalogDriven || !channelCatalog) return []
    return channelCatalog.filter((c) => c.filter_tier === "primary_default")
  }, [channelCatalog, useCatalogDriven])

  const catalogPinnedExtra = React.useMemo(() => {
    if (!useCatalogDriven || !channelCatalog) return []
    const primaryKeys = new Set(catalogPrimary.map((c) => c.key))
    return pinnedSafe
      .map((k) => channelCatalog.find((c) => c.key === k))
      .filter((e): e is ChannelCatalogEntry => Boolean(e))
      .filter((e) => e.filter_tier !== "hidden")
      .filter((e) => !primaryKeys.has(e.key))
  }, [channelCatalog, useCatalogDriven, pinnedSafe, catalogPrimary])

  const catalogAddable = React.useMemo(() => {
    if (!useCatalogDriven || !channelCatalog) return []
    const primaryKeys = new Set(catalogPrimary.map((c) => c.key))
    return channelCatalog.filter(
      (c) => c.filter_tier === "available" && !primaryKeys.has(c.key),
    )
  }, [channelCatalog, useCatalogDriven, catalogPrimary])

  const phoneCallCatalogEntry = React.useMemo(() => {
    if (!useCatalogDriven || !channelCatalog) return null
    const entry = channelCatalog.find((c) => c.key === PHONE_CALL_REGISTRY_KEY)
    if (!entry || entry.filter_tier === "hidden") return null
    return entry
  }, [channelCatalog, useCatalogDriven])

  const isPhoneCallKey = (key: string) => key === PHONE_CALL_REGISTRY_KEY

  const catalogPrimaryScrollable = catalogPrimary.filter((c) => !isPhoneCallKey(c.key))
  const catalogPinnedExtraScrollable = catalogPinnedExtra.filter((c) => !isPhoneCallKey(c.key))
  const catalogAddableScrollable = catalogAddable.filter((c) => !isPhoneCallKey(c.key))

  const legacyPhoneCallPlatform = platforms.find((p) => p.id === "phone_call")
  const legacyScrollablePlatforms = platforms.filter((p) => p.id !== "all" && p.id !== "phone_call")

  const renderCatalogChannelTab = React.useCallback(
    (entry: ChannelCatalogEntry, tooltipKey?: string) => {
      const tabId = registryKeyToSelectedId(entry.key)
      const isMetaChannel =
        entry.key === "WhatsApp" || entry.key === "Facebook" || entry.key === "Instagram"
      const isDisabled = !META_INTEGRATION_ENABLED && isMetaChannel
      const label =
        entry.key === PHONE_CALL_REGISTRY_KEY
          ? t("user_profile.calls")
          : entry.display_name || entry.label
      return (
        <Tooltip key={tooltipKey ?? entry.key}>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size={displayMode === "compact" ? "xs" : "sm"}
              className={cn(
                "flex items-center gap-2 transition-all duration-300 flex-shrink-0",
                selectedPlatform === tabId && "bg-muted",
                isDisabled && "opacity-50 cursor-not-allowed",
              )}
              onClick={() => {
                if (!isDisabled) onSelectPlatform(tabId)
              }}
              disabled={isDisabled}
            >
              {catalogChannelGlyph(entry)}
              <span
                className={cn(
                  "text-xs transition-all duration-300 whitespace-nowrap",
                  displayMode === "compact" && "w-0 overflow-hidden opacity-0",
                )}
              >
                {label}
              </span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {isDisabled
              ? "Disabled in this environment"
              : entry.key === PHONE_CALL_REGISTRY_KEY
                ? t("user_profile.calls")
                : entry.description || entry.display_name}
          </TooltipContent>
        </Tooltip>
      )
    },
    [displayMode, onSelectPlatform, selectedPlatform, t],
  )

  const formLeadsTab = platforms.find((p) => p.id === "form-leads")

  return (
    <TooltipProvider>
      <div className="flex items-center gap-2 px-4 py-2 max-w-full relative">
        <div className="flex shrink-0 items-center gap-2 border-r border-border pr-2">
          <Tooltip key="all-fixed">
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size={displayMode === "compact" ? "xs" : "sm"}
                className={cn(
                  "flex items-center gap-2 transition-all duration-300 flex-shrink-0",
                  selectedPlatform === "all" && "bg-muted",
                )}
                onClick={() => onSelectPlatform("all")}
              >
                <span className="text-xs font-medium whitespace-nowrap">{t("user_profile.all")}</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t("user_profile.all")}</TooltipContent>
          </Tooltip>

          {useCatalogDriven && phoneCallCatalogEntry
            ? renderCatalogChannelTab(phoneCallCatalogEntry, "phone-call-fixed")
            : !useCatalogDriven && legacyPhoneCallPlatform
              ? (
                <Tooltip key="phone_call-fixed">
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size={displayMode === "compact" ? "xs" : "sm"}
                      className={cn(
                        "flex items-center gap-2 transition-all duration-300 flex-shrink-0",
                        selectedPlatform === "phone_call" && "bg-muted",
                      )}
                      onClick={() => onSelectPlatform("phone_call")}
                    >
                      {legacyPhoneCallPlatform.icon}
                      <span
                        className={cn(
                          "text-xs transition-all duration-300 whitespace-nowrap",
                          displayMode === "compact" && "w-0 overflow-hidden opacity-0",
                        )}
                      >
                        {t("user_profile.calls")}
                      </span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t("user_profile.calls")}</TooltipContent>
                </Tooltip>
              )
              : null}
        </div>

        <div
          ref={scrollContainerRef}
          className={cn(
            "flex items-center gap-2 transition-all duration-300 flex-1 min-w-0",
            isMobile
              ? "overflow-x-auto"
              : cn(
                "overflow-y-visible",
                displayMode === "scroll" &&
                "overflow-x-auto scrollbar-thin scrollbar-thumb-transparent hover:scrollbar-thumb-gray-300/60 scrollbar-track-transparent scrollbar-corner-transparent group",
              ),
          )}
          style={{
            scrollbarWidth: "thin",
            scrollbarColor: displayMode === "scroll" ? "transparent transparent" : undefined,
          }}
        >
          {useCatalogDriven ? (
            <>
              {catalogPrimaryScrollable.map((entry) => renderCatalogChannelTab(entry))}

              {formLeadsTab ? (
                <Tooltip key="form-leads">
                  <TooltipTrigger asChild>
                    {(() => {
                      const isDisabled = !META_INTEGRATION_ENABLED
                      return (
                        <Button
                          variant="ghost"
                          size={displayMode === "compact" ? "xs" : "sm"}
                          className={cn(
                            "flex items-center gap-2 transition-all duration-300 flex-shrink-0",
                            selectedPlatform === "form-leads" && "bg-muted",
                            isDisabled && "opacity-50 cursor-not-allowed",
                          )}
                          onClick={() => {
                            if (!isDisabled) onSelectPlatform("form-leads")
                          }}
                          disabled={isDisabled}
                        >
                          {formLeadsTab.icon}
                          <span
                            className={cn(
                              "text-xs transition-all duration-300 whitespace-nowrap",
                              displayMode === "compact" && "w-0 overflow-hidden opacity-0",
                            )}
                          >
                            {formLeadsTab.name}
                          </span>
                        </Button>
                      )
                    })()}
                  </TooltipTrigger>
                  <TooltipContent>
                    {!META_INTEGRATION_ENABLED ? "Disabled in this environment" : formLeadsTab.name}
                  </TooltipContent>
                </Tooltip>
              ) : null}

              {catalogPinnedExtraScrollable.map((entry) =>
                renderCatalogChannelTab(entry, `pinned-${entry.key}`),
              )}

              {catalogAddableScrollable.length > 0 && onTogglePinChannelKey ? (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size={displayMode === "compact" ? "xs" : "sm"}
                      className="flex flex-shrink-0 items-center gap-1 border-dashed text-muted-foreground hover:text-foreground"
                    >
                      <Plus className="h-4 w-4" />
                      {!isMobile && <span className="text-xs whitespace-nowrap">Canales</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72" align="start">
                    <div className="mb-2 text-sm font-medium">Mostrar en la barra</div>
                    <div className="max-h-64 space-y-1 overflow-y-auto pr-1">
                      {catalogAddableScrollable.map((entry) => {
                        const isPinned = pinnedSafe.includes(entry.key)
                        return (
                          <div
                            key={entry.key}
                            className="flex items-center justify-between gap-2 rounded-md py-1"
                          >
                            <span className="flex min-w-0 items-center gap-2 text-xs">
                              {catalogChannelGlyph(entry)}
                              <span className="truncate">{entry.display_name}</span>
                            </span>
                            <Button
                              variant={isPinned ? "secondary" : "ghost"}
                              size="xs"
                              className="shrink-0"
                              onClick={() => onTogglePinChannelKey(entry.key)}
                            >
                              {isPinned ? "Quitar" : "Añadir"}
                            </Button>
                          </div>
                        )
                      })}
                    </div>
                  </PopoverContent>
                </Popover>
              ) : null}
            </>
          ) : (
            legacyScrollablePlatforms.map((platform) => (
              <Tooltip key={platform.id}>
                <TooltipTrigger asChild>
                  {(() => {
                    const isMetaPlatform = platform.id === "whatsapp" || platform.id === "form-leads"
                    const isDisabled = !META_INTEGRATION_ENABLED && isMetaPlatform
                    return (
                      <Button
                        variant="ghost"
                        size={displayMode === "compact" ? "xs" : "sm"}
                        className={cn(
                          "flex items-center gap-2 transition-all duration-300 flex-shrink-0",
                          selectedPlatform === platform.id && "bg-muted",
                          isDisabled && "opacity-50 cursor-not-allowed",
                        )}
                        onClick={() => {
                          if (!isDisabled) {
                            onSelectPlatform(platform.id)
                          }
                        }}
                        disabled={isDisabled}
                      >
                        {platform.id === "all" ? (
                          <span className="text-xs font-medium whitespace-nowrap">{t('user_profile.all')}</span>
                        ) : (
                          <>
                            {platform.icon}
                            <span
                              className={cn(
                                "text-xs transition-all duration-300 whitespace-nowrap",
                                displayMode === "compact" && "w-0 overflow-hidden opacity-0",
                              )}
                            >
                              {platform.name === "phone_call"
                                ? t('user_profile.calls')
                                : platform.name === "whatsapp"
                                  ? t('user_profile.whatsapp') || "WhatsApp"
                                  : platform.name === "web"
                                    ? t('user_profile.web')
                                    : platform.name}
                            </span>
                          </>
                        )}
                      </Button>
                    )
                  })()}
                </TooltipTrigger>
                <TooltipContent>
                  {(!META_INTEGRATION_ENABLED &&
                    (platform.id === "whatsapp" || platform.id === "form-leads"))
                    ? "Disabled in this environment"
                    : platform.name === "All"
                      ? t('user_profile.all')
                      : platform.name === "phone_call"
                        ? t('user_profile.calls')
                        : platform.name === "whatsapp"
                          ? t('user_profile.whatsapp') || "WhatsApp"
                          : platform.name === "web"
                            ? t('user_profile.web')
                            : platform.name}
                </TooltipContent>
              </Tooltip>
            ))
          )}

          {safeSavedFilters.map((sf) => (
            <PastillaConAcciones
              key={sf.id}
              size={displayMode === "compact" ? "xs" : "sm"}
              activa={selectedPlatform === sf.id}
              onClick={() => onApplyFilter && onApplyFilter({
                name: sf.name,
                icon: sf.icon,
                tags: sf.tag_names || [],
                channels: sf.channels || [],
                agents: [],
                id: sf.id,
                user_categories: sf.user_categories || []
              })}
              acciones={[
                {
                  titulo: 'Editar',
                  icono: <Pencil className="h-2.5 w-2.5" />,
                  onPulsar: () => handleEdit(sf),
                },
                {
                  titulo: 'Eliminar',
                  desactivada: deletingId === sf.id,
                  icono: deletingId === sf.id
                    ? <Loader2 className="h-3 w-3 animate-spin" />
                    : <X className="h-3 w-3" />,
                  onPulsar: () => {
                    void deleteFilterApi(sf.id).catch((err) => {
                      log.error('Failed to delete filter:', err)
                    })
                  },
                },
              ]}
            >
              {renderIcon(sf.icon, cn("h-4 w-4", sf.icon_color || (selectedPlatform === sf.id ? "text-primary" : "")))}
              <span>{sf.name}</span>
            </PastillaConAcciones>
          ))}
          <Button variant="outline" size={displayMode === "compact" ? "xs" : "sm"} onClick={() => { resetForm(); setShowBuilder(true); }} className="flex items-center gap-2 border-dashed text-muted-foreground hover:text-foreground flex-shrink-0 whitespace-nowrap mr-10">
            <Filter className="h-4 w-4" />{!isMobile && " Crear filtro"}
          </Button>
        </div>

        {/* Left scroll arrow with gradient (desktop only — touch uses native scroll) */}
        {!isMobile && displayMode === "scroll" && canScrollLeft && (
          <div className="absolute left-0 top-0 bottom-0 w-12 flex items-center pointer-events-none">
            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-background via-background/80 to-transparent pointer-events-none" />
            {/* Scroll arrow */}
            <Button
              variant="ghost"
              size="icon-xs"
              className="pointer-events-auto relative z-10 text-muted-foreground hover:text-foreground"
              onMouseDown={(e) => {
                e.preventDefault()
                const container = scrollContainerRef.current
                if (container) {
                  const scrollInterval = setInterval(() => {
                    container.scrollLeft -= 50
                  }, 50)

                  const stopScrolling = () => {
                    clearInterval(scrollInterval)
                    document.removeEventListener('mouseup', stopScrolling)
                    document.removeEventListener('mouseleave', stopScrolling)
                  }

                  document.addEventListener('mouseup', stopScrolling)
                  document.addEventListener('mouseleave', stopScrolling)
                }
              }}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
          </div>
        )}

        {/* Right scroll arrow with gradient (desktop only — touch uses native scroll) */}
        {!isMobile && displayMode === "scroll" && canScrollRight && (
          <div className="absolute right-0 top-0 bottom-0 w-12 flex items-center pointer-events-none">
            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-l from-background via-background/80 to-transparent pointer-events-none" />
            {/* Scroll arrow */}
            <Button
              variant="ghost"
              size="icon-xs"
              className="pointer-events-auto relative z-10 text-muted-foreground hover:text-foreground"
              onMouseDown={(e) => {
                e.preventDefault()
                const container = scrollContainerRef.current
                if (container) {
                  const scrollInterval = setInterval(() => {
                    container.scrollLeft += 50
                  }, 50)

                  const stopScrolling = () => {
                    clearInterval(scrollInterval)
                    document.removeEventListener('mouseup', stopScrolling)
                    document.removeEventListener('mouseleave', stopScrolling)
                  }

                  document.addEventListener('mouseup', stopScrolling)
                  document.addEventListener('mouseleave', stopScrolling)
                }
              }}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
      <Dialog open={showBuilder} onOpenChange={(open) => { setShowBuilder(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto overflow-x-hidden min-w-0 w-[calc(100vw-2rem)] sm:w-auto">
          <DialogHeader>
            <DialogTitle>{editingFilterId ? 'Editar filtro' : 'Crear filtro'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 text-sm min-w-0">
            {/* Nombre + Icono */}
            <div className="grid md:grid-cols-2 gap-4 min-w-0">
              <label className="grid gap-2">
                <span className="text-sm font-medium text-foreground">Nombre</span>
                <Input
                  value={filterName}
                  onChange={(e) => setFilterName(e.target.value)}
                  placeholder="Leads calientes WhatsApp"
                  className="bg-background border rounded-md focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                />
              </label>
              <div className="grid gap-2">
                <span className="text-sm font-medium text-foreground">Icono</span>
                <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start gap-2">
                      {renderIcon(selectedIcon.startsWith('emoji') ? `emoji:${emoji.trim()}` : selectedIcon, iconColorClass)}
                      Elegir icono
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="z-[1000] w-[min(460px,calc(100vw-2rem))] p-3" align="start" sideOffset={8}>
                    {/* El mismo selector que usa Agent Studio. Estaba escrito
                        aqui por tercera vez, con su propia lista de emoji y su
                        propia paleta. */}
                    <SelectorVisual
                      visual={{ icon: selectedIcon, colorClass: iconColorClass || undefined }}
                      iconos={ICONOS_DE_FILTRO}
                      columnasIconos={10}
                      columnasEmojis={8}
                      renderIcono={(id, color) => renderIcon(id, color)}
                      onElegido={() => setPickerOpen(false)}
                      onVisualChange={(v) => {
                        setIconColorClass(v.colorClass || '')
                        if (v.icon) setSelectedIcon(v.icon)
                        if (v.icon?.startsWith('emoji:')) setEmoji(v.icon.slice(6))
                      }}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Tags con sugerencias */}
            <div className="grid gap-2 min-w-0">
              <span className="text-sm font-medium text-foreground">Tags</span>
              <Input
                placeholder="buscar tag existente"
                value={tagSearchValue}
                onChange={(e) => setTagSearchValue(e.target.value)}
                className="bg-background border rounded-md focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const v = tagSearchValue.trim();
                    if (!v) return;
                    // Find matching tag or add as new
                    const matchingTag = fetchedTags.find(t => t.name.toLowerCase() === v.toLowerCase());
                    if (matchingTag && !tags.includes(matchingTag.name)) {
                      setTags([...tags, matchingTag.name]);
                    } else if (!tags.includes(v)) {
                      setTags([...tags, v]);
                    }
                    setTagSearchValue('');
                  }
                }}
              />
              <div className="relative min-w-0">
                <div
                  ref={suggestRef}
                  className={cn(
                    "flex flex-wrap gap-2 rounded-lg p-3 bg-muted/30 border",
                    showBorderSuggest ? "border-primary/50" : "border-transparent",
                    "scrollbar-thin scrollbar-thumb-muted-foreground/20 scrollbar-track-transparent"
                  )}
                  style={{ scrollbarWidth: showBorderSuggest ? "thin" : "none" }}
                >
                  {(() => {
                    if (filteredAvailableTags.length === 0 && tagSearchValue.trim()) {
                      return (
                        <span className="text-xs text-muted-foreground italic px-2">
                          No se encontraron tags que coincidan con "{tagSearchValue}"
                        </span>
                      );
                    }
                    
                    if (filteredAvailableTags.length === 0 && !tagSearchValue.trim() && fetchedTags.length === 0) {
                      return (
                        <span className="text-xs text-muted-foreground italic px-2">
                          No hay tags disponibles. Crea uno en el perfil de usuario.
                        </span>
                      );
                    }
                    
                    return filteredAvailableTags.map(tag => (
                      <TagBadge
                        key={tag.id}
                        id={tag.id}
                        name={tag.name}
                        color={tag.color}
                        variant="soft"
                        size="sm"
                        className="whitespace-nowrap transition-transform duration-200 hover:scale-105"
                        onClick={() => {
                          if (!tags.includes(tag.name)) {
                            setTags([...tags, tag.name]);
                            setTagSearchValue('');
                          }
                        }}
                      />
                    ));
                  })()}
                </div>
                {showBorderSuggest && (
                  <div className="pointer-events-none absolute right-0 top-0 h-full w-10 bg-gradient-to-l from-background/90 to-transparent flex items-center justify-end pr-1">
                    <ChevronRight className="h-4 w-4 text-muted-foreground/60" />
                  </div>
                )}
              </div>
            </div>

            {/* Canales y Agentes */}
            <div className="grid md:grid-cols-2 gap-4 min-w-0">
              <div className="grid gap-2">
                <span className="text-sm font-medium text-foreground">Canales</span>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 min-w-0">
                  {[
                    { id: 'web', n: 'Web', color: 'text-primary', icon: (<Globe className="h-4 w-4 text-primary" />) },
                    { id: 'betterplace', n: 'Betterplace', color: '', icon: (<Building2 className="h-4 w-4" style={estiloDeCanal('betterplace')} />) },
                    { id: 'phone_call', n: 'Llamada', color: '', icon: (<Phone className="h-4 w-4" style={estiloDeCanal('phone_call')} />) },
                    { id: 'whatsapp', n: 'WhatsApp', color: '', icon: (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke={COLOR_DE_CANAL.whatsapp}
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="h-4 w-4"
                        >
                          <path d="M3 21l1.9-5.7a8.5 8.5 0 113.8 3.8z" />
                        </svg>
                      ) },
                  ].map(ch => {
                    const active = channels.includes(ch.id)
                    return (
                      <TarjetaElegible
                        key={ch.id}
                        icono={ch.icon}
                        nombre={ch.n}
                        elegida={active}
                        onElegir={() => setChannels(active ? channels.filter(c => c !== ch.id) : [...channels, ch.id])}
                      />
                    )
                  })}
                </div>

                {/* Webhook URL Display removed */}
              </div>
              <div className="grid gap-2 min-w-0">
                <span className="text-sm font-medium text-foreground">Categoría de Cliente</span>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 min-w-0">
                  {[
                    { id: 'lead', name: 'Lead', ...COLOR_DE_CATEGORIA.lead },
                    { id: 'client', name: 'Cliente', ...COLOR_DE_CATEGORIA.client },
                    { id: 'vendor', name: 'Proveedor', ...COLOR_DE_CATEGORIA.vendor },
                    { id: 'collaborator', name: 'Colaborador', ...COLOR_DE_CATEGORIA.collaborator },
                    { id: 'employee', name: 'Empleado', ...COLOR_DE_CATEGORIA.employee },
                  ].map(cat => {
                    const active = userCategories.includes(cat.id)
                    const IconComponent = CATEGORY_ICONS[cat.id as keyof typeof CATEGORY_ICONS] || User

                    return (
                      <TarjetaElegible
                        key={cat.id}
                        icono={<IconComponent className={cn("h-4 w-4", cat.color)} />}
                        nombre={cat.name}
                        elegida={active}
                        className={active ? cat.fondo : undefined}
                        onElegir={() => setUserCategories(active ? userCategories.filter(c => c !== cat.id) : [...userCategories, cat.id])}
                      />
                    )
                  })}
                </div>
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2 pt-4">
            <Button
              variant="outline"
              onClick={() => { setShowBuilder(false); resetForm(); }}
              className="border-0 hover:border-primary focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
              disabled={isCreating}
            >
              Cancelar
            </Button>
            <Button
              onClick={async () => {
                try {
                  const filterData = createFilterData();
                  // Validate that at least one criterion is selected
                  if (!filterData.tag_names?.length && !filterData.channels?.length && !filterData.user_categories?.length) {
                    alert('Debes seleccionar al menos un criterio para el filtro (tags, canales o categorías)');
                    return;
                  }

                  if (editingFilterId) {
                    await updateFilterApi(editingFilterId, filterData);
                  } else {
                    await createFilterApi(filterData);
                  }

                  setShowBuilder(false);
                  resetForm();
                } catch (err) {
                  console.error('Failed to create/update filter:', err);
                  alert('Error al guardar el filtro. Por favor intenta de nuevo.');
                }
              }}
              disabled={isCreating || !filterName.trim()}
              className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-sm"
            >
              {isCreating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Guardando...</> : 'Guardar filtro'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TooltipProvider >
  )
}
